/*----------------------------------
- DEPENDANCES
----------------------------------*/

import React from 'react';
import { Check, Copy } from 'lucide-preact';

/*----------------------------------
- TYPES
----------------------------------*/

export interface CopyableCodeBlockProps {
    title: string;
    caption: string;
    code: string;
    testId: string;
    language?: CopyableCodeLanguage;
}

export type CopyableCodeLanguage = 'bash' | 'json' | 'text' | 'typescript' | 'javascript';

/*----------------------------------
- CONSTANTS
----------------------------------*/

const COPY_RESET_DELAY_MS = 1600;
const HTML_ESCAPE_MAP: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
};

const JSON_TOKEN_PATTERN = /("(?:\\.|[^"\\])*")(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?/g;
const TYPESCRIPT_TOKEN_PATTERN =
    /(\/\/[^\n]*|\/\*[\s\S]*?\*\/)|(`(?:\\.|[^`\\])*`|'(?:\\.|[^'\\])*'|"(?:\\.|[^"\\])*")|\b(type|interface|export|import|from|const|let|var|return|await|async|new|extends|Record|Partial|Pick|Omit|Array)\b|\b(string|number|boolean|null|undefined|unknown|object|void|Promise)\b|\b(true|false)\b|-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?/g;

/*----------------------------------
- HELPERS
----------------------------------*/

const escapeHtml = (value: string) => value.replace(/[&<>"']/g, (character) => HTML_ESCAPE_MAP[character]);

const highlightCodeByPattern = (
    code: string,
    pattern: RegExp,
    highlightToken: (match: RegExpExecArray) => string,
) => {
    let highlighted = '';
    let lastIndex = 0;
    pattern.lastIndex = 0;

    let match = pattern.exec(code);

    while (match) {
        highlighted += escapeHtml(code.slice(lastIndex, match.index));
        highlighted += highlightToken(match);
        lastIndex = match.index + match[0].length;
        match = pattern.exec(code);
    }

    highlighted += escapeHtml(code.slice(lastIndex));

    return highlighted;
};

const highlightJson = (code: string) => highlightCodeByPattern(code, JSON_TOKEN_PATTERN, (match) => {
    const [token, stringToken, keySuffix, literal] = match;

    if (stringToken && keySuffix) {
        return `<span class="text-sky-300">${escapeHtml(stringToken)}</span>${escapeHtml(keySuffix)}`;
    }

    if (stringToken) return `<span class="text-emerald-300">${escapeHtml(stringToken)}</span>`;
    if (literal) return `<span class="text-violet-300">${escapeHtml(token)}</span>`;

    return `<span class="text-amber-300">${escapeHtml(token)}</span>`;
});

const highlightTypeScript = (code: string) => highlightCodeByPattern(code, TYPESCRIPT_TOKEN_PATTERN, (match) => {
    const [token, comment, stringToken, keyword, builtInType, literal] = match;

    if (comment) return `<span class="text-slate-500">${escapeHtml(comment)}</span>`;
    if (stringToken) return `<span class="text-emerald-300">${escapeHtml(stringToken)}</span>`;
    if (keyword) return `<span class="text-violet-300">${escapeHtml(keyword)}</span>`;
    if (builtInType) return `<span class="text-sky-300">${escapeHtml(builtInType)}</span>`;
    if (literal) return `<span class="text-amber-300">${escapeHtml(literal)}</span>`;

    return `<span class="text-amber-300">${escapeHtml(token)}</span>`;
});

const highlightCode = (code: string, language: CopyableCodeLanguage) => {
    if (language === 'json') return highlightJson(code);
    if (language === 'typescript' || language === 'javascript') return highlightTypeScript(code);

    return escapeHtml(code);
};

/*----------------------------------
- COMPONENT
----------------------------------*/

export default function CopyableCodeBlock({
    title,
    caption,
    code,
    testId,
    language = 'text',
}: CopyableCodeBlockProps) {
    const [copied, setCopied] = React.useState(false);
    const resetTimeoutRef = React.useRef<number | null>(null);

    React.useEffect(() => {
        return () => {
            if (resetTimeoutRef.current !== null && typeof window !== 'undefined') {
                window.clearTimeout(resetTimeoutRef.current);
            }
        };
    }, []);

    const handleCopy = React.useCallback(async () => {
        if (typeof navigator === 'undefined' || !navigator.clipboard?.writeText) {
            return;
        }

        try {
            await navigator.clipboard.writeText(code);
            setCopied(true);
        } catch {
            return;
        }

        if (typeof window !== 'undefined') {
            if (resetTimeoutRef.current !== null) {
                window.clearTimeout(resetTimeoutRef.current);
            }

            resetTimeoutRef.current = window.setTimeout(() => {
                setCopied(false);
            }, COPY_RESET_DELAY_MS);
        }
    }, [code]);

    return (
        <div className="overflow-hidden rounded-3xl border border-slate-800 bg-slate-950 shadow-2xl">
            <div className="flex flex-col gap-3 border-b border-slate-800 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <p className="text-sm font-semibold text-white">{title}</p>
                    <p className="text-xs text-slate-400">{caption}</p>
                </div>
                <button
                    type="button"
                    onClick={() => {
                        void handleCopy();
                    }}
                    data-testid={`${testId}-copy`}
                    className="inline-flex items-center gap-2 self-start rounded-full border border-slate-700 bg-slate-900 px-3 py-2 text-sm font-medium text-slate-100 transition hover:border-orange-400 hover:text-white"
                >
                    {copied ? <Check size={16} /> : <Copy size={16} />}
                    {copied ? 'Copied' : 'Copy'}
                </button>
            </div>
            <pre
                data-testid={`${testId}-content`}
                className="overflow-x-auto px-5 py-4 text-sm leading-6 text-slate-200"
            >
                <code dangerouslySetInnerHTML={{ __html: highlightCode(code, language) }} />
            </pre>
        </div>
    );
}
